import { useContext, useEffect, useState } from "react";
import UIkit from "uikit";

import { AppContexts } from "../../../providers";
import UserSettingsService from "../../../services/user-settings.service";

const SIGNATURE_MAX_LENGTH = 160;

const UpdateSmsSignatureForm = () => {
  const { user, setUser } = useContext(AppContexts.AuthContext);
  const [settings, setSettings] = useState({
    enabled: false,
    signature_text: "",
  });
  const [status, setStatus] = useState("initial");
  const [error, setError] = useState("");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const userSignature = user?.user?.settings?.sms_signature;

    if (!!userSignature) {
      setSettings({
        enabled: userSignature.enabled || false,
        signature_text: userSignature.signature_text || "",
      });
    }

    UserSettingsService.get()
      .then((resp) => {
        const signature = resp.data?.sms_signature;

        if (!!signature) {
          setSettings({
            enabled: signature.enabled || false,
            signature_text: signature.signature_text || "",
          });
        }
      })
      .catch((err) => {
        console.warn(err);
      });
  }, [user]);

  const updateEnabled = () => {
    const enabled = !settings.enabled;

    setSettings({
      ...settings,
      enabled,
    });
    setError("");
    setDirty(true);
  };

  const updateSignatureText = (e) => {
    const value = e.target.value;

    setSettings({
      ...settings,
      signature_text: value,
    });
    setError(
      value.length > SIGNATURE_MAX_LENGTH
          ? `Signature must be ${SIGNATURE_MAX_LENGTH} characters or less.`
          : "",
    );
    setDirty(true);
  };

  const saveSignature = (e) => {
    e.preventDefault();

    const signatureText = (settings.signature_text || "").trim();

    if (settings.enabled && !signatureText) {
      return;
    }

    if (signatureText.length > SIGNATURE_MAX_LENGTH) {
      setError(`Signature must be ${SIGNATURE_MAX_LENGTH} characters or less.`);
      return;
    }

    setStatus("pending");
    UserSettingsService.set({
      sms_signature: {
        ...settings,
        signature_text: signatureText,
      },
    })
      .then((resp) => {
        const savedSignature = resp.data?.sms_signature || {
          ...settings,
          signature_text: signatureText,
        };

        setSettings(savedSignature);
        setDirty(false);
        setError("");
        setStatus("complete");

        if (!!user) {
          setUser({
            ...user,
            user: {
              ...(user.user || {}),
              settings: {
                ...(user.user?.settings || {}),
                sms_signature: savedSignature,
              },
            },
          });
        }

        UIkit.notification("Text reply signature saved.", "success");
      })
      .catch((err) => {
        console.warn(err);
        setStatus("error");
        UIkit.notification(`Failed: ${err}`, "error");
      });
  };

  const signatureText = settings.signature_text || "";
  const missingSignatureText = settings.enabled && !signatureText.trim();
  const invalidSignature = missingSignatureText || !!error;

  return (
    <form className="auth uk-form-stacked" onSubmit={saveSignature}>
      {status === "error" && (
        <p>There was an error updating your text reply signature.</p>
      )}

      <div className="uk-width-1-1 uk-margin-top uk-margin-small-bottom">
        <div className="uk-flex uk-flex-middle">
          <div className="autosend-checkbox">
            <label
              htmlFor="smsManualSignatureEnabled"
              className="setting-switch"
            >
              <input
                type="checkbox"
                id="smsManualSignatureEnabled"
                checked={settings.enabled}
                onChange={updateEnabled}
              />
              <span className="setting-slider setting-round"></span>
            </label>
          </div>
          <div className="uk-form-label uk-margin-small-left">
            Enable Signature
          </div>
        </div>
      </div>

      <div className="uk-width-5-6">
        <label className="uk-form-label">Signature Text</label>
        <div className="uk-form-controls">
          <textarea
            className={`uk-textarea ${invalidSignature ? "uk-form-danger" : ""}`}
            rows="4"
            value={signatureText}
            disabled={!settings.enabled}
            maxLength={SIGNATURE_MAX_LENGTH + 1}
            onInput={updateSignatureText}
          ></textarea>
        </div>
        <div
          className={`uk-text-small uk-margin-small-top ${
            (settings.signature_text || "").length > SIGNATURE_MAX_LENGTH
              ? "uk-text-danger"
              : "uk-text-muted"
          }`}
        >
          {(settings.signature_text || "").length}/{SIGNATURE_MAX_LENGTH} characters
        </div>
        {!!error && (
          <div className="uk-text-danger uk-text-small uk-margin-small-top">
            {error}
          </div>
        )}
      </div>

      <div className="uk-width-1-1 uk-margin-small-bottom uk-margin-top">
        <button
          className={`uk-button hms-btn hms-red-btn ${
            status === "pending" ? "disabled" : ""
          }`}
          type="submit"
          disabled={status === "pending" || !dirty || invalidSignature}
        >
          {status === "pending" ? (
            <div data-uk-spinner="ratio: 0.5;"></div>
          ) : dirty ? (
            "Save Signature"
          ) : (
            "Saved"
          )}
        </button>
      </div>
    </form>
  );
};

export default UpdateSmsSignatureForm;
